import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { generatePath, useNavigate } from 'react-router'
import { z } from 'zod'
import { Checkbox } from '@/components/commons/checkbox'
import { TextArea } from '@/components/commons/textarea'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { XCloseIcon } from '@/components/ui/icons'
import { Modal } from '@/components/ui/modal'
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle
} from '@/components/ui/modal-atoms'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { useAddPhotosetComment } from '@/features/photoset/queries/use-add-photoset-comment'
import { useUpdateStepToRetouchingQaCompleted } from '@/features/photoset/queries/use-update-step-to-retouching-qa-completed'
import type { Photoset } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { routes } from '@/routes'
import { type TKey, useTranslation } from '@/translations/i18n'

export const RetouchingQaCommentConfirmModal = ({
	photosetId,
	onClose
}: {
	photosetId: Photoset['id']
	onClose: () => void
}) => {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const retouchingQaCommentConfirmModalSchema = z
		.object({
			comment: z.string(),
			skipComment: z.boolean()
		})
		.superRefine((val, ctx) => {
			if (!(val.comment || val.skipComment)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['skipComment'],
					message: t('photoset:retouching_qa.comment_confirm_error')
				})
			}
		})

	const form = useForm({
		mode: 'all',
		resolver: zodResolver(retouchingQaCommentConfirmModalSchema),
		defaultValues: {
			comment: '',
			skipComment: false
		}
	})

	// mutations
	const {
		mutate: updateStepToNeedsReview,
		isPending: isPendingUpdateStepToNeedsReview
	} = useUpdateStepToRetouchingQaCompleted({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
			})
			navigate(
				generatePath(routes.PHOTOSET_RETOUCHING_QA_COMPLETED.path, {
					id: String(photosetId)
				})
			)
		}
	})

	const {
		mutate: addPhotosetSpaceComment,
		isPending: isPendingAddPhotosetComment
	} = useAddPhotosetComment(
		{ photosetId },
		{
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.PHOTOSET]
				})
				navigate(
					generatePath(routes.PHOTOSET_RETOUCHING_QA_COMPLETED.path, {
						id: String(photosetId)
					})
				)
			}
		}
	)

	const handleSubmit = form.handleSubmit(({ comment, skipComment }) => {
		if (!skipComment) {
			addPhotosetSpaceComment({ comment })
		}

		updateStepToNeedsReview(photosetId)
	})

	const isLoading = useMemo(
		() => isPendingUpdateStepToNeedsReview || isPendingAddPhotosetComment,
		[isPendingUpdateStepToNeedsReview, isPendingAddPhotosetComment]
	)
	const skipComment = useWatch({ control: form.control, name: 'skipComment' })

	return (
		<Modal closeIconOffset='sm' onOverlayClick={onClose} width={480}>
			<ModalHeader>
				<ButtonLegacy
					onClick={onClose}
					shape='square'
					size='sm'
					type='button'
					variant='tertiary'
				>
					<XCloseIcon size={20} />
				</ButtonLegacy>
				<h3>{t('common:comment')}</h3>
			</ModalHeader>

			<ModalBody>
				<div className='flex flex-col gap-6'>
					<ModalTitle className='text-neutral-900'>
						{t(
							'photoset:retouching_qa.comment_confirm_modal_title'
						)}
					</ModalTitle>
					<form
						className='flex w-full flex-col gap-6'
						id='confirm-comment-form'
						onSubmit={handleSubmit}
					>
						<TextArea
							disabled={skipComment}
							errorMessage={
								form.formState.errors?.comment?.message &&
								t(form.formState.errors.comment.message as TKey)
							}
							maxLength={250}
							placeholder={t(
								'photoset:retouching_qa.comment_confirm_modal_placeholder'
							)}
							rows={6}
							{...form.register('comment')}
						/>

						<Controller
							control={form.control}
							name='skipComment'
							render={({ field }) => (
								<Checkbox
									errorMessage={
										form.formState.errors?.skipComment
											?.message
									}
									label={t(
										'photoset:retouching_qa.skip_comment_label'
									)}
									{...form.register('skipComment')}
									onChange={() => {
										form.setValue(
											'skipComment',
											!field.value
										)
										form.setValue('comment', '')
									}}
								/>
							)}
						/>
					</form>
				</div>
			</ModalBody>

			<Separator orientation='horizontal' />

			<ModalFooter>
				<ButtonLegacy
					disabled={isLoading}
					onClick={onClose}
					type='button'
					variant='tertiary'
				>
					{t('common:cancel')}
				</ButtonLegacy>
				<ButtonBrandLegacy
					disabled={isLoading}
					form='confirm-comment-form'
					type='submit'
				>
					{t('common:confirm')}
					{isLoading && <Spinner size={16} />}
				</ButtonBrandLegacy>
			</ModalFooter>
		</Modal>
	)
}
